/**
 * Centralized merge-tag FORMAT contract for dynamic fields.
 *
 * A merge tag lives inside `{ }` as pipe-separated, trimmed segments:
 *   - Segment[0] is the field path.
 *   - A `format:<id>[:<arg>]` segment selects a formatter.
 *   - Any other extra segment is the existing default value.
 *
 * Canonical serialization:
 *   - no format            → `{path}`
 *   - with a format        → `{path|format:<id>}`
 *   - with format+default  → `{path|<default>|format:<id>}`
 *
 * This module owns three things so the web side and the parallel API work stay
 * reconcilable in one place:
 *   1. the formatter registry + which ids apply to which `PropertyType` group,
 *   2. {@link formatMergeTagValue} — a client-side mirror of the server
 *      formatting semantics, used to preview example values in the chip popover,
 *   3. token (de)serialization helpers ({@link parseMergeTagInner} /
 *      {@link buildMergeTagInner}).
 *
 * Server-side formatting is applied at send time by the API; the previews here
 * are best-effort mirrors so marketers can sanity-check what recipients get.
 */
import { DateTime } from "luxon";

/** Formatter families, keyed off a field's data type. */
export type MergeTagFormatGroup = "string" | "number" | "currency" | "date" | "boolean" | "list";

/** A selectable format option surfaced in the chip popover. */
export interface MergeTagFormatChoice {
  /** The format id (with any baked-in arg), e.g. `upper`, `decimal:2`, `currency`. */
  value: string;
  /** Human label with an inline example of the output. */
  label: string;
}

/** Parsed representation of a merge tag's inner `{ }` content. */
export interface ParsedMergeTag {
  path: string;
  /** Formatter id + optional arg, e.g. `upper`, `decimal:2`, `currency:EUR`. */
  format?: string;
  /** Existing default value segment, preserved as-is. */
  defaultValue?: string;
}

const STRING_CHOICES: MergeTagFormatChoice[] = [
  { value: "upper", label: "UPPERCASE" },
  { value: "lower", label: "lowercase" },
  { value: "capitalize", label: "Capitalize first" },
  { value: "title", label: "Title Case" },
];

// The NUMBER group intentionally also offers percent + currency, which is where
// those formatters are "sensible" per the contract.
const NUMBER_CHOICES: MergeTagFormatChoice[] = [
  { value: "number", label: "Number (1,234.5)" },
  { value: "integer", label: "Integer (1,235)" },
  { value: "decimal:2", label: "Decimal (1,234.50)" },
  { value: "percent", label: "Percent (25%)" },
  { value: "currency", label: "Currency ($1,234.50)" },
];

const DATE_CHOICES: MergeTagFormatChoice[] = [
  { value: "date", label: "7/7/2026" },
  { value: "date_med", label: "Jul 7, 2026" },
  { value: "date_long", label: "Tuesday, July 7, 2026" },
  { value: "datetime", label: "7/7/2026 9:41 AM" },
  { value: "time", label: "9:41 AM" },
  { value: "weekday_date", label: "Tuesday 7/7" },
];

const BOOLEAN_CHOICES: MergeTagFormatChoice[] = [
  { value: "yes_no", label: "Yes / No" },
  { value: "true_false", label: "True / False" },
];

// Array fields (e.g. the tables on a reservation) join their items. The API
// trims the separator argument, so a separator with spaces cannot be
// expressed; the default ", " is the only useful choice.
const LIST_CHOICES: MergeTagFormatChoice[] = [{ value: "list", label: "Comma list (A, B, C)" }];

/**
 * Map a field's `PropertyType` (see `@/api/types/list`) to its format group.
 * Returns `null` for types with no sensible formatter (enum/unknown), in
 * which case the popover shows no format control.
 */
export function mergeTagFormatGroupForType(type?: string): MergeTagFormatGroup | null {
  switch ((type ?? "").toLowerCase()) {
    case "array":
      return "list";
    case "date":
    case "datetime":
    case "time":
      return "date";
    case "number":
    case "integer":
    case "float":
    case "decimal":
      return "number";
    case "currency":
    case "money":
      return "currency";
    case "string":
    case "text":
      return "string";
    case "boolean":
      return "boolean";
    default:
      return null;
  }
}

/**
 * The selectable format choices for a field type, or `null` when no format
 * control should be shown. Callers prepend their own "Default (no formatting)"
 * entry (serialized as an empty format).
 */
export function mergeTagFormatChoicesForType(type?: string): MergeTagFormatChoice[] | null {
  const group = mergeTagFormatGroupForType(type);
  if (!group) return null;
  switch (group) {
    case "string":
      return STRING_CHOICES;
    // A currency field is a number field that leads with money formatting; both
    // share the same set (which already includes currency + percent).
    case "number":
    case "currency":
      return NUMBER_CHOICES;
    case "date":
      return DATE_CHOICES;
    case "boolean":
      return BOOLEAN_CHOICES;
    case "list":
      return LIST_CHOICES;
    default:
      return null;
  }
}

// ── Token (de)serialization ────────────────────────────────────────────────

const FORMAT_PREFIX = "format:";

/**
 * Parse the inner `{ }` content into `{ path, format?, defaultValue? }`. Extra
 * `format:` segments win; the first non-format extra segment is the default.
 * Segments are trimmed per the contract.
 */
export function parseMergeTagInner(inner: string): ParsedMergeTag {
  const segments = (inner ?? "").split("|").map((segment) => segment.trim());
  const path = segments[0] ?? "";
  let format: string | undefined;
  let defaultValue: string | undefined;
  for (const segment of segments.slice(1)) {
    if (segment.startsWith(FORMAT_PREFIX)) {
      const id = segment.slice(FORMAT_PREFIX.length);
      if (id) format = id;
    } else if (defaultValue === undefined && segment.length > 0) {
      defaultValue = segment;
    }
  }
  return { path, format, defaultValue };
}

/**
 * Build canonical inner `{ }` content from parsed parts. Order matches the
 * contract: `path`, then default (if any), then `format:<id>` (if any).
 */
export function buildMergeTagInner({ path, format, defaultValue }: ParsedMergeTag): string {
  const parts: string[] = [path];
  if (defaultValue) parts.push(defaultValue);
  if (format) parts.push(`${FORMAT_PREFIX}${format}`);
  return parts.join("|");
}

// ── Value formatting (client-side mirror of server semantics) ───────────────
//
// This section is a deliberate byte-for-byte mirror of the merged API formatter
// (`apps/api/src/lib/merge-tag-format.ts`, PR #175). Keep the two in lock-step:
// `stringify`, `joinList`, `capitalize`, `titleCase`, `toNumber`, `formatNumber`,
// `parseDateValue`/`fromEpoch`, `DATE_FORMATS`, the falsy-string set, and the
// per-id switch below all match the API implementation.
//
// PREVIEW-VS-SERVER DIVERGENCE (unavoidable client-side): date/time formatters
// need a timezone. At send time the API resolves the recipient/gym timezone
// (`resolveMergeTagTimeZone`) and only falls back to America/New_York when none
// is available. Callers thread the GYM's configured timezone into these previews
// so they render in the gym zone (matching gym-level sends); only genuine
// per-recipient timezone overrides can still differ, which the client cannot
// know. When no timezone is supplied we fall back to the API's America/New_York
// default, so the no-timezone behavior is unchanged.

/**
 * Split a stored format id into its `[base, arg]` on the FIRST colon, matching
 * how the API splits a `format:<id>:<arg>` directive (e.g. `decimal:2` →
 * `["decimal", "2"]`, `currency:EUR` → `["currency", "EUR"]`).
 */
function splitFormatId(formatId: string): [string, string] {
  const idx = formatId.indexOf(":");
  if (idx === -1) return [formatId, ""];
  return [formatId.slice(0, idx), formatId.slice(idx + 1)];
}

/** Server fallback timezone, used when no timezone is supplied; see the divergence note above. */
const DEFAULT_TIME_ZONE = "America/New_York";

/** Mirrors the API `stringify`: null/undefined → "", Date → ISO, else String(). */
function stringify(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

/** Mirrors the API `joinList`: arrays join item by item, a scalar is wrapped. */
function joinList(value: unknown, separator: string): string {
  const items = Array.isArray(value) ? value : [value];
  return items.map((item) => stringify(item)).join(separator);
}

function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function titleCase(value: string): string {
  // Split on whitespace while preserving the whitespace tokens so spacing is
  // untouched; capitalize the first character of each word, leave the rest.
  return value
    .split(/(\s+)/)
    .map((part) => (/^\s+$/.test(part) ? part : capitalize(part)))
    .join("");
}

/**
 * Coerce to a finite number, or `null` when not numeric. Mirrors the API: no
 * comma stripping (`Number("1,234")` is NaN → raw passthrough) and `Number("")`
 * is `0`.
 */
function toNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatNumber(value: unknown, options: Intl.NumberFormatOptions): string {
  const n = toNumber(value);
  // NaN / non-numeric → output the raw stringified value unchanged (API parity).
  if (n === null) return stringify(value);
  return new Intl.NumberFormat("en-US", options).format(n);
}

function fromEpoch(epoch: number, zone: string): DateTime | null {
  if (!Number.isFinite(epoch)) return null;
  // Values below 1e12 are assumed to be seconds; otherwise milliseconds.
  const millis = Math.abs(epoch) < 1e12 ? epoch * 1000 : epoch;
  const dt = DateTime.fromMillis(millis, { zone });
  return dt.isValid ? dt : null;
}

/**
 * Parse a raw value into a Luxon `DateTime` in `zone`, mirroring the API: only
 * `Date` instances, epoch numbers, integer-string epochs, and ISO strings are
 * recognized (no RFC2822 / arbitrary `new Date()` parsing), so unparseable
 * values degrade to the raw string exactly as they do server-side.
 */
function parseDateValue(value: unknown, zone: string): DateTime | null {
  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value, { zone });
    return dt.isValid ? dt : null;
  }
  if (typeof value === "number") {
    return fromEpoch(value, zone);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^-?\d+$/.test(trimmed)) return fromEpoch(Number(trimmed), zone);
    const iso = DateTime.fromISO(trimmed, { zone });
    return iso.isValid ? iso : null;
  }
  return null;
}

function formatDate(value: unknown, zone: string, tokenFormat: string): string {
  const dt = parseDateValue(value, zone);
  return dt ? dt.toFormat(tokenFormat) : stringify(value);
}

const DATE_FORMATS: Record<string, string> = {
  date: "M/d/yyyy",
  date_med: "MMM d, yyyy",
  date_long: "cccc, MMMM d, yyyy",
  datetime: "M/d/yyyy h:mm a",
  time: "h:mm a",
  weekday_date: "cccc M/d",
};

// Strings that count as FALSE (trimmed, case-insensitive) — mirrors the API PR
// (#175) deviation from the literal spec. Every other string is truthy.
const STRING_FALSY = new Set(["false", "0", "no", "off", ""]);

/**
 * Coerce a raw value to a boolean, matching send-time semantics: strings use the
 * {@link STRING_FALSY} allowlist, everything else uses normal JS truthiness (so
 * `0`, `false`, `null`, `undefined` are false; other values true).
 */
function toBoolean(value: unknown): boolean {
  if (typeof value === "string") {
    return !STRING_FALSY.has(value.trim().toLowerCase());
  }
  return Boolean(value);
}

/**
 * Format a raw sampled value for a given formatter id, mirroring the server's
 * send-time semantics. Unknown ids and unparseable values fall back to the raw
 * string (no-op), matching the contract.
 *
 * Date/time formatters render in `options.timeZone` (an IANA zone, e.g. the
 * gym's configured timezone) so previews match gym-level sends. When no
 * timezone is supplied, they fall back to the API's {@link DEFAULT_TIME_ZONE}.
 */
export function formatMergeTagValue(
  raw: unknown,
  formatId?: string,
  options?: { timeZone?: string },
): string {
  if (!formatId) return stringify(raw);

  const zone = options?.timeZone?.trim() || DEFAULT_TIME_ZONE;
  const [base, arg] = splitFormatId(formatId);
  switch (base) {
    // ── STRING ──
    case "upper":
      return stringify(raw).toUpperCase();
    case "lower":
      return stringify(raw).toLowerCase();
    case "capitalize":
      return capitalize(stringify(raw));
    case "title":
      return titleCase(stringify(raw));

    // ── LIST ──
    // Joins array items with the (trimmed) arg, ", " when no arg is given; a
    // scalar renders as-is. `list:` with an empty arg joins with "" like the API.
    case "list":
      return joinList(raw, formatId.includes(":") ? arg.trim() : ", ");

    // ── NUMBER ──
    case "number":
      return formatNumber(raw, { maximumFractionDigits: 2 });
    case "integer":
      return formatNumber(raw, { maximumFractionDigits: 0 });
    case "decimal": {
      // API default: 0 fraction digits when the arg is missing/invalid.
      const digits = Number(arg);
      const fractionDigits = Number.isInteger(digits) && digits >= 0 ? digits : 0;
      return formatNumber(raw, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    }
    case "percent":
      return formatNumber(raw, { style: "percent", maximumFractionDigits: 2 });

    // ── CURRENCY ──
    case "currency": {
      const code = (arg || "USD").toUpperCase();
      const n = toNumber(raw);
      if (n === null) return stringify(raw);
      try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: code }).format(n);
      } catch {
        // Invalid ISO 4217 code → fall back to USD (API parity, not raw).
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
      }
    }

    // ── DATE ──
    // The `utc` arg reads calendar days stored at UTC midnight in UTC, so
    // they do not shift to the previous day west of UTC.
    case "date":
    case "date_med":
    case "date_long":
    case "datetime":
    case "time":
    case "weekday_date":
      return formatDate(raw, arg.trim().toLowerCase() === "utc" ? "utc" : zone, DATE_FORMATS[base]);

    // ── BOOLEAN ──
    case "yes_no":
      return toBoolean(raw) ? "Yes" : "No";
    case "true_false":
      return toBoolean(raw) ? "True" : "False";

    // Unknown id → no-op (raw stringified value).
    default:
      return stringify(raw);
  }
}
